import { Input } from '@/components/form/Input';
import { Container } from '@/components/ui';
import { SUBSCRIPTION_INPUT_CLASS } from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

export interface NegotiatedPriceInputProps {
  value: number;
  onChange: (value: number) => void;
  msrp?: number;
  usagePrice?: number;
  floorPrice?: number;
  disabled?: boolean;
  className?: string;
  title?: string;
}

/**
 * NegotiatedPriceInput - Reusable component for negotiated price input with discount calculation
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export function NegotiatedPriceInput({
  value,
  onChange,
  msrp,
  usagePrice,
  floorPrice,
  disabled = false,
  className,
  title = 'Negotiated Price',
}: NegotiatedPriceInputProps) {
  const renderDiscount = (label: string, referencePrice?: number) => {
    if (!referencePrice || referencePrice <= 0 || value <= 0) return null;

    const discountPercent = ((referencePrice - value) / referencePrice) * 100;
    const isDiscount = discountPercent > 0;
    const isPremium = discountPercent < 0;
    const isHighDiscount = isDiscount && discountPercent > 10;
    const isHighPremium = isPremium && discountPercent < -10;

    return (
      <div className="text-xs">
        <span className="text-chariot-text-secondary">
          {isDiscount ? 'Discount from' : 'Premium over'} {label}:{' '}
        </span>
        <span
          className={`${
            isHighDiscount
              ? 'text-chariot-status-critical font-bold'
              : isHighPremium
                ? 'text-chariot-status-success font-bold'
                : isPremium
                  ? 'text-chariot-status-success'
                  : 'text-chariot-status-warning'
          }`}
        >
          {isDiscount
            ? `${Math.abs(discountPercent).toFixed(1)}%`
            : `+${Math.abs(discountPercent).toFixed(1)}%`}
        </span>
      </div>
    );
  };

  return (
    <section className={cn('space-y-4', className)}>
      <h3 className="text-chariot-text-primary text-lg font-semibold">
        {title}
      </h3>
      <Container isSelected className="bg-chariot-background-primary">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-chariot-text-primary text-sm font-medium">
              Negotiated Annual Price
            </h2>
            <div className="mt-2 flex items-end gap-3">
              <div className="space-y-1">
                {renderDiscount('MSRP', msrp)}
                {renderDiscount('Usage', usagePrice)}
              </div>
              {floorPrice &&
                floorPrice > 0 &&
                value > 0 &&
                value < floorPrice && (
                  <span className="text-chariot-status-critical text-[10px] font-bold whitespace-nowrap">
                    Below floor (${floorPrice.toLocaleString()})
                  </span>
                )}
            </div>
          </div>
          <div className="text-chariot-brand-primary text-2xl font-bold">
            <Input
              name="negotiatedPrice"
              className={cn(SUBSCRIPTION_INPUT_CLASS, 'text-right')}
              value={value.toString()}
              onChange={e =>
                !disabled && onChange(parseInt(e.target.value) || 0)
              }
              placeholder="Enter price"
              disabled={disabled}
            />
          </div>
        </div>
      </Container>
    </section>
  );
}
